import { SocialPostType } from "../edit-post-with-gallery/types";
import HeartLogo from "@/assets/heart.svg?react"
import HeartFillLogo from "@/assets/heart-filled.svg?react"
import { Dialog, DialogContent, DialogTrigger } from "@/shared/ui/dialog.tsx"
import { useGetCatalog, useGetDefaultGallery, useGetGallery, useGetVideoGallery, useGetWishlist } from "./api";
// import ImageSelection from "./components/ImageSelection";
import { useState } from "react";
import ImageGrid from "./components/ImageGrid";
import { Button } from "../ui/button.tsx";
import { Check, ChevronLeft, Search, SlidersHorizontal, Upload, Video, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEditImageCarousel, useSaveImage } from "./api/carousel";
import UploadImageInput from "./components/UploadImageInput";
import { Input } from "../ui/input.tsx";
import { toast } from "sonner";
import { useAtomValue } from "jotai";
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms.ts";

type Props = {
  content: SocialPostType["content"][number],
  story_id: number,
  event_id: number,
  trigger: JSX.Element,
  onSave?: (url: string) => void,
  isCarousel: boolean,
  selectedImages: { image_id: number, image_url: string, type: string }[]
}

const GallerySelector = ({ trigger, selectedImages: currentImages, event_id, isCarousel, story_id }: Props) => {

  const [open, setOpen] = useState(false)

  const theme = useAtomValue(themeAtom)

  // current view ( gallery | favourites | upload | videos )
  const [view, setView] = useState<"gallery" | "favourites" | "upload" | "videos">("gallery")

  const { gallery: videos, hasNextPage: hasNextVideo, fetchNextPage: fetchNextVideos } = useGetVideoGallery(open && view === "videos");

  const { catalogData } = useGetCatalog(true)

  const [showSearch, setShowSearch] = useState(false)

  const [searchValue, setSearchValue] = useState("")

  const { gallery, hasNextPage, fetchNextPage } = useGetDefaultGallery(open)

  const fetchNextPageHandler = () => {
    if (view === "favourites") {
      fetchNextWishlist()
    } else if (view === "gallery" && catalogFilter !== undefined) {
      fetchNextCatalogImage()
    } else if (view === "videos") {
      fetchNextVideos()
    } else {
      fetchNextPage()
    }
  }

  const { wishlist, hasNextPage: hasNextWishlist, fetchNextPage: fetchNextWishlist } = useGetWishlist(open)

  // catalogue selection
  const [catalogFilter, setCatalogFilter] = useState<string | undefined>(undefined)

  const { gallery: galleryImages, hasNextPage: hasNextCatalogImage, fetchNextPage: fetchNextCatalogImage } = useGetGallery(true, catalogFilter)

  // selected image array
  const [selectedImages, setSelectedImages] = useState<{ image_id: number, image_url: string, type: string }[]>(currentImages)

  const toggleImageHandler = (selection: { image_id: number, image_url: string, type: string }) => {
    const isSelected = selectedImages.findIndex(img => img.image_id === selection.image_id)
    if (isSelected !== -1) {
      setSelectedImages(prev => prev.filter(img => img.image_id !== selection.image_id))
    } else {
      if (isCarousel) {
        setSelectedImages(prev => [...prev, selection])
      } else {
        setSelectedImages([selection])
      }
    }
  }


  const { editImageCarousel, editCarouselAsync } = useEditImageCarousel()

  const { saveImage } = useSaveImage()

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-5xl flex flex-col gap-2 pt-5 h-[90vh] overflow-hidden !rounded-3xl p-0">
        <button
          type="button"
          onClick={() => {
            if (selectedImages.length === 0) {
              toast.message("Select atleast one image");
              return;
            }
            if (isCarousel && selectedImages.length > 0) {
              // call the save carousel api here
              if (selectedImages.length === 1) {
                editCarouselAsync({ post_id: event_id, imageIds: null, story_id: story_id }).then(() => {
                  saveImage({
                    event_id: event_id,
                    story_id: story_id,
                    image_id: selectedImages[0].image_id
                  })
                })
              } else {
                editImageCarousel({ post_id: event_id, story_id: story_id, imageIds: selectedImages.map(img => img.image_id) })
              }
            } else if (selectedImages.length > 0) {
              saveImage({
                event_id: event_id,
                story_id: story_id,
                image_id: selectedImages[0].image_id
              })
            }
            setOpen(false)
          }}
          className="absolute top-2.5 right-10 size-7 bg-white hover:bg-gray-100 flex items-center justify-center rounded-full">
          <Check className="" size={15} />
        </button>

        {view !== "upload" &&
          <>
            <div className="px-6 pt-6 flex items-center gap-2 w-full">
              {selectedImages.map(image => (
                <div className="size-16 rounded-lg relative group" key={image.image_id}>
                  {image.type === "video"
                    ? <video src={image.image_url} className="object-cover rounded-lg" />
                    : <img className="object-cover rounded-lg" src={image.image_url} />
                  }
                  <Button
                    onClick={() => {
                      setSelectedImages(prev => {
                        const newImgs = prev.filter(img => img.image_id !== image.image_id)
                        return newImgs;
                      })
                    }}
                    className="absolute -top-1 -right-1 rounded-full transition-all ease-in-out size-4 hidden group-hover:flex bg-red-400 text-white hover:bg-red-500 hover:text-white" variant="outline" size="icon"><X className="scale-50" /></Button>
                </div>
              ))}
            </div>
            <div className="px-6 flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => {
                    setCatalogFilter(undefined)
                    setView("gallery")
                  }}
                  variant="outline" size="icon" className="rounded-full"><ChevronLeft /></Button>
                <p className="text-xl font-montserrat tracking-wide font-medium">Choose from Gallery</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setView("favourites")}
                  className={cn("ring-0 outline-0 shadow-none border-0 rounded-full", view === "favourites" && "bg-purple-200")} size="icon" variant="outline">
                  {view === "favourites" ?
                    <HeartFillLogo className="text-red-400" fill="red" /> : <HeartLogo />
                  }
                </Button>
                <Button
                  onClick={() => {
                    setShowSearch(prev => !prev)
                  }}
                  className="rounded-full ring-0 outline-0 shadow-none border-0" size="icon" variant="outline">
                  <Search />
                </Button>
                <Button
                  onClick={() => {
                    if (theme.isVideoClient) {
                      setView("videos")
                    } else {
                      toast.message("Coming soon 🎃")
                    }
                  }}
                  className="rounded-full ring-0 outline-0 shadow-none border-0" size="icon" variant="outline">
                  <Video />
                </Button>
                <Button
                  onClick={() => {
                    if (catalogData) {
                      setCatalogFilter(catalogData.catalogue[0].unique_identifier)
                    }
                  }}
                  className="rounded-full ring-0 outline-0 shadow-none border-0" size="icon" variant="outline">
                  <SlidersHorizontal />
                </Button>
                <Button
                  onClick={() => setView("upload")}
                  className="rounded-full ring-0 outline-0 shadow-none border-0" size="icon" variant="outline">
                  <Upload />
                </Button>
              </div>
            </div>
            <div className="w-full flex flex-wrap items-center gap-2 px-5">
              {showSearch && (
                <div className="w-full flex items-center gap-2">
                  <Input onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = e.target.value
                    setSearchValue(newValue)
                  }} value={searchValue} className="flex-1" />
                  <Button title="reset" variant="outline" size="icon" className="rounded-full" onClick={() => {
                    setSearchValue("")
                  }}><X /></Button>
                </div>
              )}
              {catalogData && catalogFilter && catalogData.catalogue.map(cat => (
                <div key={cat.unique_identifier}
                  onClick={() => setCatalogFilter(cat.unique_identifier)}
                  className={cn("font-poppins text-gray-500 text-sm cursor-pointer tracking-wide rounded-full border border-purple-200 px-4 py-1",
                    catalogFilter === cat.unique_identifier && "border-purple-500 font-medium"
                  )}>
                  {cat.name}
                </div>
              ))}
            </div>
            <ImageGrid
              value={searchValue}
              galleryImages={view === "favourites" ? wishlist : catalogFilter !== undefined ? galleryImages : view === "videos" ? videos : gallery}
              hasNextPage={view === "favourites" ? hasNextWishlist : catalogFilter !== undefined ? hasNextCatalogImage : view === "videos" ? hasNextVideo : hasNextPage}
              fetchNextPage={fetchNextPageHandler}
              onSelect={toggleImageHandler}
              selectedImages={selectedImages} />
          </>
        }

        {view === "upload" && (
          <>
            <div className="px-6 flex w-full items-center justify-between pt-7">
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setView("gallery")}
                  variant="outline" size="icon" className="rounded-full"><ChevronLeft /></Button>
                <p className="text-xl font-montserrat tracking-wide font-medium">Upload custom image</p>
              </div>
            </div>
            {catalogData &&
              <UploadImageInput catalogData={catalogData.catalogue} setShowUpload={() => setView("gallery")} onSelect={(val: { image_url: string, image_id: number, type: string }) => {
                if (isCarousel) {
                  setSelectedImages(prev => [...prev, val])
                } else {
                  setSelectedImages([val])
                }
              }} />
            }
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GallerySelector;

